import React, { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import PromptIntro from "../components/wizards/custom/PromptIntro";
import LandingSections from "../components/landing/LandingSections";
import MainLayout from "../components/layout/MainLayout";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../hooks/useTranslation";
import { CLERK_ENABLED } from "../lib/clerk";
import { safeReturnUrl } from "../lib/returnUrl";
import {
  BOT_CREATE_INTENT_KEY,
  SIGNUP_STARTED_AT_KEY,
  trackEvent,
  trackSignupIfNew,
} from "../lib/analytics";

// Stesse chiavi di CreateBotPage: la landing scrive, il wizard legge.
const MODE_STORAGE_KEY = "pingup-onboarding-mode";
const INTRO_STORAGE_KEY = "pingup-onboarding-intro";

const loadIntro = () => {
  try {
    const raw = sessionStorage.getItem(INTRO_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/**
 * Landing pubblica (onboarding v2): chiunque raggiunga PingUp atterra qui e
 * può scrivere il prompt SENZA essere registrato — come Lovable. Al click su
 * "Crea il mio assistente": se autenticato si entra subito nel wizard; se no,
 * con Clerk attivo si apre la MODALE di registrazione in overlay sulla
 * landing (il prompt resta lì sotto) e a sessione creata si entra nel wizard
 * senza ricaricare la pagina — così sopravvivono anche i File allegati.
 * Fallback legacy (Clerk spento): si passa dalla pagina /register e il draft
 * (testo+sito, in sessionStorage) viene ripreso al ritorno.
 */
const StartPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  // Stessa fonte di verità di ProtectedRoute/PublicRoute (AuthContext, che
  // vive alla radice dell'app): lo stato di login sopravvive alle navigazioni
  // senza ricontrolli per-pagina, quindi niente "flash da sloggato" tornando
  // sulla landing da autenticati.
  const { isAuthenticated, isLoading, refresh } = useAuth();
  const filesRef = useRef([]);
  // Contenitore del prompt: i pulsanti "prova" della landing qui sotto ci
  // riportano su e mettono il cursore nel campo.
  const promptRef = useRef(null);
  // La modale può emettere più eventi di sessione: si entra nel wizard una
  // volta sola.
  const enteredRef = useRef(false);
  // Velo di transizione post-modale: appena la sessione Clerk nasce, copre
  // la landing PRIMA che l'AuthContext si aggiorni — senza, per la durata di
  // /auth/me si rivedrebbe per un attimo la variante da sloggato.
  const [handingOff, setHandingOff] = useState(false);
  // Destinazione da raggiungere a login avvenuto, arrivata da /login nello
  // state (es. la pagina di consenso /connect/authorize?… del plugin
  // WordPress). Catturata al mount: lo state di history viene ripulito
  // subito sotto per non riaprire la modale al refresh.
  const returnUrlRef = useRef(safeReturnUrl(location.state?.returnUrl));
  // Fermato al mount: il ref si svuota quando si parte verso la destinazione.
  const [conDestinazione] = useState(() => Boolean(returnUrlRef.current));

  // Ascolta la nascita della sessione Clerk (registrazione o accesso dalla
  // modale): aggiorna l'AuthContext e, se c'è un draft del prompt, entra nel
  // wizard via router (niente reload → i File in memoria restano validi).
  // Senza draft non si naviga: il refresh fa comparire la shell da loggato.
  useEffect(() => {
    if (!CLERK_ENABLED) return undefined;
    let unsubscribe = null;
    let cancelled = false;

    const attach = () => {
      if (cancelled) return;
      // clerk-js si carica in modo asincrono: riprova finché non c'è.
      if (!window.Clerk?.addListener) {
        setTimeout(attach, 300);
        return;
      }
      unsubscribe = window.Clerk.addListener(({ session, user }) => {
        if (!session) {
          // Logout senza lasciare la pagina: si riarma per il prossimo login.
          enteredRef.current = false;
          return;
        }
        if (enteredRef.current) return;
        enteredRef.current = true;
        trackSignupIfNew(user || window.Clerk?.user);
        // Sincrono, prima di qualsiasi await: il velo appare subito.
        setHandingOff(true);
        (async () => {
          try {
            await refresh();
            // NIENTE avvio automatico dell'analisi: si atterra di nuovo sul
            // campo prompt (prefillato dal draft) nella variante da loggato,
            // ed è l'utente a premere "Crea il mio assistente".
            sessionStorage.removeItem(MODE_STORAGE_KEY);
            // Lascia alla shell il tempo di dipingersi SOTTO il velo prima
            // di rivelarla: senza, la sidebar "sbucherebbe" a scatto.
            await new Promise((resolve) => setTimeout(resolve, 300));
          } catch (error) {
            console.error("Handoff post-modale fallito:", error);
            enteredRef.current = false;
          }
          setHandingOff(false);
        })();
      });
    };
    attach();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Arrivo da /login o /register (con Clerk attivo quelle rotte reindirizzano
  // qui): si apre la modale richiesta SOPRA la landing col prompt — l'auth è
  // sempre un overlay, mai una pagina a sé. Lo state viene ripulito subito,
  // così un refresh non riapre la modale.
  useEffect(() => {
    if (!CLERK_ENABLED || isLoading || isAuthenticated) return undefined;
    const kind = location.state?.clerkModal;
    if (kind !== "signIn" && kind !== "signUp") return undefined;

    // Pulizia dello state SENZA passare dal router: una replace-navigation
    // qui farebbe richiudere la modale appena aperta. history diretto = il
    // refresh non la riaprirà e la modale non viene disturbata.
    const historyState = window.history.state;
    if (historyState?.usr) {
      window.history.replaceState({ ...historyState, usr: null }, "");
    }

    let cancelled = false;
    const open = () => {
      if (cancelled) return;
      if (!window.Clerk?.openSignIn) {
        setTimeout(open, 300);
        return;
      }
      if (kind === "signIn") window.Clerk.openSignIn();
      else window.Clerk.openSignUp();
    };
    open();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, isLoading, isAuthenticated]);

  // Ritorno alla destinazione richiesta: appena la sessione c'è — nata dalla
  // modale (il listener sopra ha già fatto refresh()) o già presente — si va
  // lì, una volta sola. Senza returnUrl decide il render qui sotto: la dashboard,
  // o il prompt se ce n'e' uno scritto prima di registrarsi.
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    const target = returnUrlRef.current;
    if (!target) return;
    returnUrlRef.current = null;
    navigate(target, { replace: true });
  }, [isAuthenticated, isLoading, navigate]);

  const handleSubmit = ({ description, websiteUrl, fileSources }) => {
    trackEvent("bot_ai_submit");
    sessionStorage.setItem(BOT_CREATE_INTENT_KEY, String(Date.now()));
    sessionStorage.setItem(
      INTRO_STORAGE_KEY,
      JSON.stringify({ description, websiteUrl })
    );
    sessionStorage.setItem(MODE_STORAGE_KEY, websiteUrl ? "auto" : "prompt");
    // Nuovo giro dalla landing: un draft del wizard rimasto da un tentativo
    // precedente non completato farebbe atterrare il wizard sul riepilogo/
    // config di quel tentativo invece che sul prompt/sito appena inseriti
    // (vedi stesso fix in CreateBotPage.setMode).
    sessionStorage.removeItem("pingup-onboarding-draft");
    filesRef.current = fileSources || [];

    if (isAuthenticated) {
      // Autenticato: dritto nel wizard, con i File ancora in memoria.
      navigate("/bots/new", { state: { introFiles: filesRef.current } });
    } else if (CLERK_ENABLED && window.Clerk) {
      // Stile Lovable: la registrazione è una modale sopra la landing, il
      // prompt resta visibile sotto. L'ingresso nel wizard lo fa il listener
      // qui sopra appena la sessione esiste.
      sessionStorage.setItem(SIGNUP_STARTED_AT_KEY, String(Date.now()));
      window.Clerk.openSignUp();
    } else {
      // Fallback legacy: pagina di registrazione, draft in storage.
      navigate("/register");
    }
  };

  const handleManual = () => {
    sessionStorage.setItem(MODE_STORAGE_KEY, "custom");
    sessionStorage.removeItem(INTRO_STORAGE_KEY);
    // Stesso motivo di handleSubmit: si sta iniziando un giro nuovo.
    sessionStorage.removeItem("pingup-onboarding-draft");
    if (isAuthenticated) {
      navigate("/bots/new");
    } else if (CLERK_ENABLED && window.Clerk) {
      window.Clerk.openSignUp();
    } else {
      navigate("/register");
    }
  };

  // "Accedi" nell'header: con Clerk attivo apre la modale di accesso (stessa
  // esperienza della registrazione); fallback legacy sulla pagina /login.
  const handleLogin = (event) => {
    if (CLERK_ENABLED && window.Clerk) {
      event.preventDefault();
      window.Clerk.openSignIn();
    }
  };

  // "Prova il bot gratis" e gli altri pulsanti di prova della landing: si
  // torna su al prompt, dove nasce l'assistente.
  const handleTry = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    promptRef.current
      ?.querySelector("textarea")
      ?.focus({ preventScroll: true });
  };

  // Velo di transizione post-modale: un OVERLAY sfocato SOPRA la pagina,
  // non una pagina sostitutiva — sotto resta la landing, la shell con la
  // sidebar si dipinge coperta, e il velo si toglie solo a shell pronta:
  // niente sidebar che "sbuca" a scatto.
  const handoffVeil = handingOff ? (
    <div
      data-handoff-veil
      className="pu-veil-in fixed inset-0 z-[60] flex items-center justify-center bg-white/60 backdrop-blur-sm"
    >
      <style>{`
        @keyframes puVeilIn { from { opacity: 0; } to { opacity: 1; } }
        .pu-veil-in { animation: puVeilIn .15s ease-out both; }
      `}</style>
      <Loader2 className="h-8 w-8 animate-spin text-[#64748B]" />
    </div>
  ) : null;

  // Utente autenticato (anche appena entrato dalla modale) senza un prompt
  // scritto prima di registrarsi ne' una destinazione: dritto nella
  // dashboard. /home, a chi non ha ancora assistenti, apre la creazione.
  if (isAuthenticated && !conDestinazione && !loadIntro()) {
    return <Navigate to="/home" replace />;
  }

  // Utente autenticato con il prompt in sospeso: stessa pagina prompt come
  // prima schermata, ma dentro la shell con sidebar e rotte.
  if (isAuthenticated) {
    return (
      <>
        {handoffVeil}
        <MainLayout>
          <PromptIntro
            inShell
            initialDraft={loadIntro()}
            initialFiles={filesRef.current}
            onSubmit={handleSubmit}
            onManual={handleManual}
          />
        </MainLayout>
      </>
    );
  }

  // Primo onboarding (non autenticato): fullscreen senza sidebar né rotte,
  // solo il prompt sul mesh gradient — la registrazione arriva al "Crea".
  // L'header galleggia sopra lo sfondo (che copre l'intero viewport).
  // Sotto il prompt, scorrendo, la landing del sito (LandingSections).
  return (
    <>
      {handoffVeil}
      <div ref={promptRef} className="relative min-h-screen">
      <header className="absolute inset-x-0 top-0 z-10 flex items-center justify-between px-6 py-4">
        <img
          src="/pingup-logo-horizontal.svg"
          alt="PingUp"
          className="h-8"
        />
        {!isLoading && (
          <a
            href="/login"
            onClick={handleLogin}
            className="text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            {t("onboardingWizard.startLogin")}
          </a>
        )}
      </header>

      <PromptIntro
        initialDraft={loadIntro()}
        initialFiles={filesRef.current}
        onSubmit={handleSubmit}
        onManual={handleManual}
      />
      </div>
      <LandingSections onTry={handleTry} />
    </>
  );
};

export default StartPage;
